import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from './client'

// ── Stripe inventory shapes ────────────────────────────────────────

export interface StripePrice {
  id: string
  product_id: string | null
  nickname: string | null
  type: string | null
  recurring_interval: string | null
  billing_scheme: string | null
  tiers_mode: string | null
  unit_amount: number | null
  unit_amount_decimal: string | null
  currency: string | null
  active: boolean
  metered: boolean
  meter: string | null
  active_for_new_orgs: boolean
}

export interface StripeProduct {
  id: string
  name: string
  description: string | null
  active: boolean
  default_price_id: string | null
  prices: StripePrice[]
}

export interface StripeMeter {
  id: string
  display_name: string | null
  event_name: string
  status: string | null
  aggregation_formula: string | null
  customer_mapping_event_payload_key: string | null
  value_settings_event_payload_key: string | null
}

// ── Pricing config ────────────────────────────────────────────────

export interface PricingConfig {
  // v7: per-minute sell price (replaced markup_multiplier — Stripe's flat
  // Price is the source of truth; this should track that rate).
  minutes_price_usd_per_minute: number
  storage_free_gb: number
  storage_block_gb: number
  storage_price_usd_per_block: number
  // v6: wholesale rate the operator pays (Railway/S3, etc). Folded into
  // the Overview tab COST card alongside per-minute cost.
  storage_cost_usd_per_gb_month: number
  storage_strategy: 'flat_block' | 'bundled_retention' | string
  metered_minutes_price_id: string | null
  storage_price_id: string | null
  storage_meter_event_name: string
  storage_reporter_enabled: boolean
  active_price_ids: string[]
  effective_formula_text: string
  updated_at: number
  updated_by_user_id: string | null
}

export type PricingConfigUpdate = Partial<{
  minutes_price_usd_per_minute: number
  storage_free_gb: number
  storage_block_gb: number
  storage_price_usd_per_block: number
  storage_cost_usd_per_gb_month: number
  storage_strategy: 'flat_block' | 'bundled_retention'
  metered_minutes_price_id: string | null
  storage_price_id: string | null
  storage_meter_event_name: string
  storage_reporter_enabled: boolean
  active_price_ids: string[]
}>

// ── Subscription items (per-org) ──────────────────────────────────

export interface SubscriptionItemPrice {
  id: string
  nickname: string | null
  unit_amount: number | null
  currency: string | null
  recurring_interval: string | null
  metered: boolean
}

export interface SubscriptionItem {
  id: string
  price: SubscriptionItemPrice
  quantity: number | null
  created: number | null
}

export interface OrgSubscriptionItemsView {
  org_id: string
  subscription_id: string
  status: string
  items: SubscriptionItem[]
}

// ── Per-org storage ───────────────────────────────────────────────

export interface StorageSnapshot {
  id: string
  measured_at: number
  measured_date: string
  sqlite_bytes: number
  recording_bytes: number
  blob_bytes: number
  total_bytes: number
  reported_to_stripe_at: number | null
}

export interface OrgStorageDetail {
  org_id: string
  org_name: string | null
  org_slug: string | null
  latest: StorageSnapshot | null
  history: StorageSnapshot[]
}

export interface OrgStorageRow {
  org_id: string
  org_name: string | null
  org_slug: string | null
  sqlite_gb: number
  recording_gb: number
  blob_gb: number
  total_gb: number
  measured_date: string | null
  reported_to_stripe_at: number | null
}

const STRIPE_BASE = '/api/admin/billing/stripe'

// ── Org list (with Stripe state) ──────────────────────────────────

export interface StripeOrgRow {
  id: string
  name: string
  slug: string
  status: string
  stripe_customer_id: string | null
  stripe_subscription_id: string | null
  stripe_subscription_status: string | null
  created_at: number
}

export function useStripeOrgs() {
  return useQuery({
    queryKey: ['admin', 'stripe', 'orgs'],
    queryFn: () =>
      apiFetch<{ orgs: StripeOrgRow[] }>(`${STRIPE_BASE}/orgs`),
  })
}

// ── Inventory hooks ───────────────────────────────────────────────

export function useStripeProducts() {
  return useQuery({
    queryKey: ['admin', 'stripe', 'products'],
    queryFn: () =>
      apiFetch<{ products: StripeProduct[]; active_price_ids: string[] }>(
        `${STRIPE_BASE}/products`,
      ),
  })
}

export function useStripePrices() {
  return useQuery({
    queryKey: ['admin', 'stripe', 'prices'],
    queryFn: () =>
      apiFetch<{ prices: StripePrice[]; active_price_ids: string[] }>(
        `${STRIPE_BASE}/prices`,
      ),
  })
}

export function useStripeMeters() {
  return useQuery({
    queryKey: ['admin', 'stripe', 'meters'],
    queryFn: () =>
      apiFetch<{ meters: StripeMeter[] }>(`${STRIPE_BASE}/meters`),
  })
}

// ── Pricing config hooks ──────────────────────────────────────────

export function usePricingConfig() {
  return useQuery({
    queryKey: ['admin', 'stripe', 'pricing-config'],
    queryFn: () => apiFetch<PricingConfig>(`${STRIPE_BASE}/pricing-config`),
  })
}

export function useUpdatePricingConfig() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: PricingConfigUpdate) =>
      apiFetch<PricingConfig>(`${STRIPE_BASE}/pricing-config`, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'stripe'] })
    },
  })
}

// ── Per-org subscription items ────────────────────────────────────

export function useOrgSubscriptionItems(orgId: string | undefined) {
  return useQuery({
    queryKey: ['admin', 'stripe', 'sub-items', orgId ?? null],
    enabled: !!orgId,
    queryFn: () =>
      apiFetch<OrgSubscriptionItemsView>(
        `${STRIPE_BASE}/orgs/${orgId}/subscription/items`,
      ),
  })
}

export function useAttachPrice() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ orgId, priceId }: { orgId: string; priceId: string }) =>
      apiFetch<SubscriptionItem>(
        `${STRIPE_BASE}/orgs/${orgId}/subscription/items`,
        { method: 'POST', body: JSON.stringify({ price_id: priceId }) },
      ),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['admin', 'stripe', 'sub-items', vars.orgId] })
    },
  })
}

export function useDetachPrice() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ orgId, itemId }: { orgId: string; itemId: string }) =>
      apiFetch<{ deleted: boolean; item_id: string }>(
        `${STRIPE_BASE}/orgs/${orgId}/subscription/items/${itemId}`,
        { method: 'DELETE' },
      ),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['admin', 'stripe', 'sub-items', vars.orgId] })
    },
  })
}

// ── Storage hooks ─────────────────────────────────────────────────

export function useStorageOrgs() {
  return useQuery({
    queryKey: ['admin', 'stripe', 'storage', 'orgs'],
    queryFn: () =>
      apiFetch<{ orgs: OrgStorageRow[] }>(`${STRIPE_BASE}/orgs/storage`),
  })
}

export function useOrgStorage(orgId: string | undefined) {
  return useQuery({
    queryKey: ['admin', 'stripe', 'storage', 'org', orgId ?? null],
    enabled: !!orgId,
    queryFn: () =>
      apiFetch<OrgStorageDetail>(`${STRIPE_BASE}/orgs/${orgId}/storage`),
  })
}

export function useTriggerSnapshot() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () =>
      apiFetch<{ upserts: number }>(`${STRIPE_BASE}/storage/snapshot-now`, {
        method: 'POST',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'stripe', 'storage'] }),
  })
}

export function useTriggerStorageReport() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () =>
      apiFetch<{ reported: number }>(`${STRIPE_BASE}/storage/report-now`, {
        method: 'POST',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'stripe', 'storage'] }),
  })
}

// ── Stripe Dashboard deep-link ─────────────────────────────────────
//
// Test-mode aware. Sandbox accounts (e.g. acct_1TQtKW2R9yXJsUp3) live in
// dashboard.stripe.com/test/...; live accounts at dashboard.stripe.com/...
// We don't have direct access to the account id at runtime — the backend
// could expose it but for v1 we treat any test-key environment as test
// mode. The plan documents flipping the URL prefix in one place when
// live mode is enabled.
const STRIPE_DASHBOARD_TEST_PREFIX = 'https://dashboard.stripe.com/test'

export function stripeDashboardUrl(path: string): string {
  const cleaned = path.startsWith('/') ? path : `/${path}`
  return `${STRIPE_DASHBOARD_TEST_PREFIX}${cleaned}`
}
